import { call, put } from 'redux-saga/effects';
import axios from 'axios';
import sagaActions from '../../actions/index';
import {
  fetchDashboardRequest,
  fetchDashboardSuccess,
  fetchDashboardFailure,
  deleteVerificationRequest,
  deleteVerificationSuccess,
  deleteVerificationFailure,
  fetchFeedbackRequest,
  fetchFeedbackSuccess,
  fetchFeedbackFailure,
  submitFeedbackRequest,
  submitFeedbackSuccess,
  submitFeedbackFailure,
  downloadCsvRequest,
  downloadCsvSuccess,
  downloadCsvFailure,
} from '../../slices/superAdminDashboardSlice';
import { TRACKER_API_BASE } from '../../../BaseUrl/BaseUrl';

function authHeaders() {
  const token = localStorage.getItem('auth-token');
  return {
    headers: {
      'x-auth-token': token,
    },
  };
}

function statusToQueryParam(status) {
  if (!status) return null;
  const s = String(status).toLowerCase();
  if (s === 'all') return null;
  if (s === 'pending') return 'pending=true';
  if (s === 'progress') return 'progress=true';
  if (s === 'working') return 'working=true';
  return `${s}=true`;
}

export function* fetchDashboardSaga(action) {
  try {
    yield put(fetchDashboardRequest());
    const { page = 1, limit = 10, status, search } = action.payload || {};

    const statusParams = statusToQueryParam(status);
    const searchParam =
      search && String(search).trim() ? `&searchText=${encodeURIComponent(String(search).trim())}` : '';

    const response = yield call(
      axios.get,
      `${TRACKER_API_BASE}/admin-actions/verification-status?page=${page}&limit=${limit}${
        statusParams ? `&${statusParams}` : ''
      }${searchParam}`,
      authHeaders()
    );
    const data = response?.data?.data;
    yield put(fetchDashboardSuccess(data));
  } catch (error) {
    yield put(fetchDashboardFailure(error?.response?.data?.message || 'Something went wrong'));
  }
}

export function* deleteVerificationSaga(action) {
  const { companyId, refetch } = action.payload || {};
  if (companyId == null) {
    yield put(deleteVerificationFailure('Missing company id'));
    return;
  }

  try {
    yield put(deleteVerificationRequest(companyId));
    const response = yield call(axios.delete, `${TRACKER_API_BASE}/admin-actions/company/${companyId}`, authHeaders());
    const message = response?.data?.meta?.message || response?.data?.message || 'Deleted successfully';
    yield put(deleteVerificationSuccess(message));
    if (refetch) {
      yield put({ type: sagaActions.SUPER_ADMIN_DASHBOARD, payload: refetch });
    }
  } catch (error) {
    yield put(deleteVerificationFailure(error?.response?.data?.message || error?.message || 'Delete failed'));
  }
}

export function* getFeedbackSaga(action) {
  const { companyId } = action.payload || {};
  try {
    yield put(fetchFeedbackRequest());
    const response = yield call(axios.get, `${TRACKER_API_BASE}/feedback/company/${companyId}`, authHeaders());
    yield put(fetchFeedbackSuccess(response?.data?.data?.FeedBack ?? null));
  } catch (error) {
    // 404 means no feedback yet — treat as empty, not an error
    if (error?.response?.status === 404) {
      yield put(fetchFeedbackSuccess(null));
    } else {
      yield put(fetchFeedbackFailure(error?.response?.data?.message || 'Failed to load feedback'));
    }
  }
}

export function* postFeedbackSaga(action) {
  const { companyId, feedback, refetch } = action.payload || {};
  try {
    yield put(submitFeedbackRequest());
    const response = yield call(
      axios.post,
      `${TRACKER_API_BASE}/feedback/company/${companyId}`,
      { feedback },
      authHeaders()
    );
    const message = response?.data?.meta?.message || response?.data?.message || 'Feedback submitted';
    yield put(submitFeedbackSuccess(message));
    if (refetch) {
      yield put({ type: sagaActions.SUPER_ADMIN_DASHBOARD, payload: refetch });
    }
  } catch (error) {
    yield put(submitFeedbackFailure(error?.response?.data?.message || 'Failed to submit feedback'));
  }
}

export function* downloadExcelSaga() {
  try {
    yield put(downloadCsvRequest());

    const response = yield call(axios.get, `${TRACKER_API_BASE}/admin-actions/download`, {
      ...authHeaders(),
      responseType: 'blob',
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;

    link.setAttribute('download', 'company-details.xlsx');

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);

    yield put(downloadCsvSuccess());
  } catch (error) {
    yield put(downloadCsvFailure(error?.message || 'Download failed'));
  }
}
